import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MainLayout } from '@/layouts/MainLayout';
import { Stack } from '@/components/ui/Stack';
import { ScrollToTopButton, PageHeader, Container } from '@/components/ui';
import { Button } from '@/components/ui/Button';
import { FixedDaysHeader } from '@/components/fixedDays/FixedDaysHeader';
import { FixedDaysGauge } from '@/components/fixedDays/FixedDaysGauge';
import { FixedDaysBar } from '@/components/fixedDays/FixedDaysBar';
import { useFixedDaysStore } from '@/store/fixedDaysStore';
import type { FixedDaysPool } from '@/types/fixedDays.types';

export function FixedDaysPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    pools,
    summaries,
    participants,
    participantsSummary,
    histories,
    fileExplanation,
    selectedPool,
    selectedHistory,
    selectedParticipant,
    loading,
    loadingHistories,
    loadingParticipants,
    fetchPools,
    fetchHistories,
    fetchSummary,
    fetchFile,
    fetchParticipants,
    fetchParticipantsSummary,
    setSelectedPool,
    setSelectedHistory,
    setSelectedParticipant,
    resetSelections,
  } = useFixedDaysStore();

  // Unified loading flag to avoid showing blank UI during initial fetch/hydration.
  // Only consider data loading when we have pools — if pools is empty after loading
  // completes, selectedHistory will never be set, so we must not include !selectedHistory.
  const isDataLoading = loading || loadingHistories || (pools.length > 0 && !selectedHistory);

  // True when the pool fetch has completed and returned no results
  const isEmpty = !loading && pools.length === 0;

  // Initialize data on mount and reset on unmount.
  // The widget and the page share the same store, so we clear any leftover
  // selections on entry to guarantee URL params / first-pool init take effect
  // instead of whatever the widget (or a previous page visit) left behind.
  useEffect(() => {
    resetSelections();
    fetchPools();
    return () => {
      resetSelections();
    };
  }, [fetchPools, resetSelections]);

  // Set selected pool from query params or default to first pool (only on initial load)
  useEffect(() => {
    if (pools.length === 0) return;
    // Only set pool if none is selected (initial load)
    if (selectedPool) return;

    const poolIdParam = searchParams.get('poolId');
    let pool: FixedDaysPool | undefined;

    if (poolIdParam) {
      pool = pools.find((p) => p.poolId === parseInt(poolIdParam, 10));
    }

    if (!pool) {
      pool = pools[0];
    }

    setSelectedParticipant(null);
    setSelectedPool(pool);
  }, [pools, searchParams, selectedPool, setSelectedParticipant, setSelectedPool]);

  // Fetch histories when pool changes
  useEffect(() => {
    if (!selectedPool) return;
    // Let the store handle caching logic - always call fetch
    fetchHistories(selectedPool.poolId);
  }, [selectedPool, fetchHistories]);

  // Set selected history from query params or default to first in histories (only on initial load)
  useEffect(() => {
    if (!selectedPool || histories.length === 0) return;
    // Only set history if none is selected (initial load or after pool change cleared it)
    if (selectedHistory) return;

    const fileIdParam = searchParams.get('fileId');
    let fileId: number;

    if (fileIdParam) {
      const parsedFileId = parseInt(fileIdParam, 10);
      // Check if the fileId exists in histories
      const historyExists = histories.find((h) => h.uniqueFileID === parsedFileId);
      fileId = historyExists ? parsedFileId : histories[0].uniqueFileID;
    } else {
      fileId = histories[0].uniqueFileID;
    }

    setSelectedHistory(fileId);
  }, [selectedPool, histories, searchParams, selectedHistory, setSelectedHistory]);

  // Fetch data when history changes (consolidated effect to prevent double loading)
  useEffect(() => {
    if (!selectedHistory || !selectedPool) return;

    // Update URL
    setSearchParams(
      {
        poolId: selectedPool.poolId.toString(),
        fileId: selectedHistory.toString(),
      },
      { replace: true }
    );

    // Let the store handle caching logic - always call fetch
    fetchSummary(selectedHistory);
    fetchFile(selectedHistory);
    fetchParticipants(selectedHistory);
  }, [selectedHistory, selectedPool, fetchSummary, fetchFile, fetchParticipants, setSearchParams]);

  // Fetch participant summary when participant changes
  useEffect(() => {
    if (!selectedHistory) return;
    fetchParticipantsSummary(selectedHistory, selectedParticipant || undefined);
  }, [selectedHistory, selectedParticipant, fetchParticipantsSummary]);

  // Get the year from selected history or fallback to selected pool year
  const selectedHistoryYear = useMemo(() => {
    const linkName = histories?.find((item) => item.uniqueFileID === selectedHistory)?.linkName;
    const match = linkName?.match(/\b(20\d{2})\b/);
    return match?.[1] || selectedPool?.year;
  }, [histories, selectedHistory, selectedPool]);

  // Handle pool change
  const handlePoolChange = (pool: FixedDaysPool) => {
    setSelectedPool(pool);
    setSelectedParticipant(null);
    // Note: histories will be fetched by useEffect when selectedPool changes
    // and then selectedHistory will be set by another useEffect
  };

  // Handle history change
  const handleHistoryChange = (fileId: number) => {
    setSelectedHistory(fileId);
    setSelectedParticipant(null);
  };

  // Handle participant change
  const handleParticipantChange = (participant: string | null) => {
    setSelectedParticipant(participant);
  };

  // Handle Excel download
  const handleDownload = () => {
    if (!summaries || summaries.length === 0) return;

    const formatNumber = (value: number | null | undefined, decimals = 0) => {
      if (value === null || value === undefined) return '';
      return value.toFixed(decimals);
    };

    // Build CSV rows
    const rows = summaries.map((item) => {
      const row: Record<string, string> = {
        Period: item.rowLabel,
        'Weighted Days %': formatNumber(item.pwFixedPercent, 0) + '%',
        'Fixed Rate (Spot)': formatNumber(item.spotRateInclRelet, 0),
        'Free Rate (Estimated)': formatNumber(item.estimatedRate, 0),
        'Weighted Avg. Total': formatNumber(item.watEstimated, 0),
        'Spot Days': formatNumber(item.pwSpotDays, 2),
        'Unfixed Days': formatNumber(item.pwUnfixedDays, 2),
        'Available Days': formatNumber(item.pwAvailableDays, 2),
      };

      // Add CM Onhire columns if applicable
      if (hasCmOnhire) {
        row['On Hire Days'] = formatNumber(item.cmOnhireDays, 0);
        row['On Hire TCE'] = formatNumber(item.cmOnhireTce, 0);
      }

      // Add participant data if selected
      const participantData = participantsSummary[item.sectionID];
      if (participantData) {
        row['Period Pool Points'] = formatNumber(participantData.periodPoolPoints, 2);
        row['Avg Earnings'] = formatNumber(participantData.avgEarnings, 0);
      }

      return row;
    });

    // Convert to CSV
    const headers = Object.keys(rows[0] || {});
    const csvContent = [
      headers.join(','),
      ...rows.map((row) => headers.map((header) => row[header] || '').join(',')),
    ].join('\n');

    // Download
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fixed-days-${selectedPool?.pool || 'export'}-${Date.now()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // Show On Hire columns if any summary row has non-zero onhire data
  const hasCmOnhire = summaries.some(
    (s) =>
      s.cmOnhireDays > 0 ||
      (s.cmOnhireTce !== null && s.cmOnhireTce !== undefined && s.cmOnhireTce > 0)
  );

  // Organize summaries by section
  const fullYearData = summaries.find((s) => s.sectionName === 'Year');
  const quarterlyData = summaries.filter((s) => s.sectionName === 'Quarter');
  const monthlyData = summaries.filter((s) => s.sectionName === 'Month');

  return (
    <MainLayout>
      <Container>
        <div className="space-y-6">
          <Stack spacing={6}>
            <PageHeader title="Fixed Days" />

            <FixedDaysHeader
              pools={pools}
              selectedPool={selectedPool}
              onPoolChange={handlePoolChange}
              histories={histories}
              selectedHistory={selectedHistory}
              onHistoryChange={handleHistoryChange}
              participants={participants}
              selectedParticipant={selectedParticipant}
              onParticipantChange={handleParticipantChange}
              onDownload={handleDownload}
              isLoading={loading}
              isLoadingHistories={loadingHistories}
            />

            {/* Empty State — pools returned an empty array */}
            {isEmpty && (
              <div className="py-12 text-center">
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                  />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
                  No pools available
                </h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  No Fixed Days pools are currently available for your account.
                </p>
                <div className="mt-6">
                  <Button onClick={() => fetchPools(true)} size="sm">
                    Retry
                  </Button>
                </div>
              </div>
            )}

            {/* Full Year View */}
            {!isEmpty && (
              <div className="mb-4 sm:mb-6 lg:mb-8">
                <FixedDaysGauge
                  data={fullYearData}
                  title={`FULL YEAR (${selectedHistoryYear || ''})`}
                  design="full"
                  participantSummary={
                    selectedParticipant && fullYearData
                      ? participantsSummary[fullYearData.sectionID]
                      : undefined
                  }
                  participantName={selectedParticipant}
                  hasCmOnhire={hasCmOnhire}
                  isLoading={isDataLoading && !fullYearData}
                  isLoadingParticipantsSummary={loadingParticipants}
                />
              </div>
            )}

            {/* File Explanation */}
            {!isEmpty && fileExplanation && fileExplanation.trim().length > 0 && (
              <div className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-700 dark:border-dark-700 dark:bg-dark-800 dark:text-gray-300 sm:p-5 sm:text-base">
                <p className="whitespace-pre-line">{fileExplanation}</p>
              </div>
            )}

            {/* Monthly and Quarterly Views */}
            {!isEmpty && (
              <div className="grid gap-4 sm:gap-5 lg:grid-cols-2 lg:gap-6">
                {/* Monthly View - Left Column */}
                <div>
                  <h2 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white sm:mb-3 sm:text-xl lg:hidden">
                    Monthly
                  </h2>
                  {isDataLoading && monthlyData.length === 0
                    ? Array.from({ length: 12 }).map((_, index) => (
                        <FixedDaysBar
                          key={`skeleton-${index}`}
                          data={undefined}
                          title=""
                          isLoading={true}
                        />
                      ))
                    : monthlyData.map((data) => (
                        <FixedDaysBar
                          key={data.sectionID}
                          data={data}
                          title={data.rowLabel}
                          participantSummary={
                            selectedParticipant ? participantsSummary[data.sectionID] : undefined
                          }
                          participantName={selectedParticipant}
                          hasCmOnhire={hasCmOnhire}
                          isLoadingParticipantsSummary={loadingParticipants}
                        />
                      ))}
                </div>

                {/* Quarterly View - Right Column */}
                <div>
                  <h2 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white sm:mb-3 sm:text-xl lg:hidden">
                    Quarterly
                  </h2>
                  <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:gap-4 lg:[grid-template-columns:repeat(auto-fit,minmax(280px,1fr))]">
                    {isDataLoading && quarterlyData.length === 0
                      ? Array.from({ length: 4 }).map((_, index) => (
                          <FixedDaysGauge
                            key={`skeleton-q${index}`}
                            data={undefined}
                            title=""
                            design="compact"
                            isLoading={true}
                          />
                        ))
                      : quarterlyData.map((data) => (
                          <FixedDaysGauge
                            key={data.sectionID}
                            data={data}
                            title={data.rowLabel}
                            design="compact"
                            participantSummary={
                              selectedParticipant ? participantsSummary[data.sectionID] : undefined
                            }
                            participantName={selectedParticipant}
                            hasCmOnhire={hasCmOnhire}
                            isLoadingParticipantsSummary={loadingParticipants}
                          />
                        ))}
                  </div>
                </div>
              </div>
            )}
          </Stack>
          <ScrollToTopButton
            threshold={200}
            className="fixed bottom-0 right-0 z-50 m-3 mr-4 lg:hidden"
          />
        </div>
      </Container>
    </MainLayout>
  );
}
